import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import {
  NOTIFICATION_SOUND_DEDUP_TTL_MS,
  NotificationSoundDedup,
} from "../helpers/notification-sound-dedup";
import {
  extractDemandeForRoute,
  resolvePrestataireDemandRoute,
  resolvePrestataireDevisResumePath,
} from "../helpers/prestataire-demand-route";
import { BobToastService } from "../shared/toast";
import { ClientService } from "./client.service";
import { LocalStorageService } from "./localStorage.service";
import { NavigationPrepService } from "./navigation-prep.service";
import { ProviderService } from "./provider.service";
import { PushScreenRefreshService } from "./push-screen-refresh.service";
import { PusherBackground } from "./pusher-background.service";

/** Aligné sur `universal_links.md` — query param pour forcer refetch écran cible. */
export const PUSH_REFRESH_QUERY_PARAM = "pushRefresh";

const SPLASH_ROUTE_FRAGMENT = "/splash";
const POLL_MS = 150;
const MAX_WAIT_MS = 120_000;
const DEEP_LINK_SCHEME = "bobexpress";

/** Events réservés au rôle client (particulier). */
const CLIENT_ONLY_EVENTS = new Set([
  "ASSIGNED_DEMANDE_CLIENT",
  "PROVIDER_ACCEPTED_DEMANDE",
  "DESTINATION_ATTENT_CLIENT",
  "NEW_ANNOUNCE",
]);

/** Events réservés au rôle prestataire. */
const PRESTATAIRE_ONLY_EVENTS = new Set([
  "NEW_DEMANDE",
  "NEW_PRE_ASSIGN_DEMANDE",
  "POP_PRE_ASSIGN_COMMAND",
  "CONFIRM_ARRIVAL_PRESTATAIRE",
  "INFORM_PRESTATAIRE_DEMANDE",
  "SIGNAL_PROBLEM_PROVIDER",
  "ONROAD_DEMANDE_CANCELED",
]);

const PRESTATAIRE_DEMAND_ROUTE_EVENTS = new Set([
  "INFORM_PRESTATAIRE_DEMANDE",
  "SIGNAL_PROBLEM_PROVIDER",
  "CONFIRM_ARRIVAL_PRESTATAIRE",
  "CHANGE_STATUT_DEVIS",
  "APPLY_PROVIDER_DEVIS",
]);

const PRESTATAIRE_DEVIS_EVENTS = new Set([
  "CHANGE_STATUT_DEVIS",
  "APPLY_PROVIDER_DEVIS",
  "CREATION_DEVIS_CLIENT",
]);

type NavigationParams = Record<string, string>;
type GuardResult = "ok" | "unavailable" | "canceled" | "finished" | "role_mismatch";

@Injectable({
  providedIn: "root",
})
export class PushNavigationService {
  private readonly bobToast = inject(BobToastService);
  private readonly translate = inject(TranslateService);
  private readonly clientService = inject(ClientService);
  private readonly navigationPrep = inject(NavigationPrepService);
  private readonly pushScreenRefresh = inject(PushScreenRefreshService);

  private queue: Record<string, any>[] = [];
  private draining = false;
  private readonly recentPushNavKeys = new Map<string, number>();

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private providerService: ProviderService
  ) {}

  enqueueFromPushPayload(data: Record<string, any> | null | undefined): void {
    if (!data || Object.keys(data).length === 0) {
      return;
    }
    const normalized = this.normalizePayload(data);
    if (this.isDuplicatePushNavigation(normalized)) {
      if (!environment.production) {
        console.log("[PushNavigationService] enqueue skipped (dedup):", this.summarizePushData(normalized));
      }
      return;
    }
    if (!environment.production) {
      console.log("[PushNavigationService] enqueue:", this.summarizePushData(normalized));
    }
    this.queue.push(normalized);
    void this.drain();
  }

  async consumeNativePendingPushClick(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      const pending = await PusherBackground.getPendingPushClick();
      if (pending?.hasPending && pending.data) {
        this.enqueueFromPushPayload(pending.data as Record<string, any>);
      }
    } catch (e) {
      console.warn("[PushNavigationService] getPendingPushClick failed:", e);
    }
  }

  shouldRefreshFromPayload(data: Record<string, any>): boolean {
    const flag = data["should_refresh"];
    return flag === true || flag === "true" || flag === "1";
  }

  private async drain(): Promise<void> {
    if (this.draining) {
      return;
    }
    this.draining = true;
    try {
      while (this.queue.length > 0) {
        const data = this.queue.shift();
        if (!data) {
          continue;
        }
        await this.waitUntilReady();
        await this.processPayload(data);
      }
    } finally {
      this.draining = false;
    }
  }

  private async waitUntilReady(): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < MAX_WAIT_MS) {
      const onSplash = (this.router.url || "").includes(SPLASH_ROUTE_FRAGMENT);
      const hasToken = !!this.localStorageService.getToken();
      if (!onSplash && hasToken) {
        return;
      }
      await this.delay(POLL_MS);
    }
    console.warn("[PushNavigationService] Timeout waiting for app ready — proceeding anyway");
  }

  private async processPayload(data: Record<string, any>): Promise<void> {
    if (this.isDuplicatePushNavigation(data)) {
      return;
    }

    const type = this.resolveNotificationType(data);
    if (type === "POP_PRE_ASSIGN_COMMAND" && !data["deep_link"] && !data["navigation_screen"]) {
      return;
    }

    this.applySideEffects(data, type);

    const path = this.resolvePath(data, type);
    if (!path) {
      console.warn("[PushNavigationService] No route resolved for push:", type);
      return;
    }

    const guardedPath = await this.guardNavigationPath(path, data, type);
    if (!guardedPath) {
      return;
    }

    this.markPushNavigationHandled(data);
    await this.navigateToTarget(guardedPath, data);
  }

  private async navigateToTarget(
    path: string,
    data: Record<string, any>
  ): Promise<void> {
    const currentPath = this.normalizeRoutePath(this.router.url);
    const targetPath = this.normalizeRoutePath(path);
    const shouldRefresh = this.shouldRefreshFromPayload(data);

    if (currentPath === targetPath) {
      if (shouldRefresh) {
        this.pushScreenRefresh.emit(targetPath, data);
      }
      return;
    }

    await this.navigationPrep.prepareForExternalNavigation("push");

    let url = path;
    if (shouldRefresh) {
      const separator = path.includes("?") ? "&" : "?";
      url = `${path}${separator}${PUSH_REFRESH_QUERY_PARAM}=${Date.now()}`;
    }

    try {
      await this.router.navigateByUrl(url, { replaceUrl: true });
    } catch (e) {
      console.warn("[PushNavigationService] navigateByUrl failed:", e);
      await this.navigateToSafeHome("unavailable");
    }
  }

  private normalizeRoutePath(url: string): string {
    const withoutQuery = (url || "").split("?")[0].split("#")[0];
    const decoded = decodeURIComponent(withoutQuery);
    if (decoded.length > 1 && decoded.endsWith("/")) {
      return decoded.slice(0, -1);
    }
    return decoded || "/";
  }

  private buildPushDedupKey(data: Record<string, any>): string {
    const eventId = NotificationSoundDedup.normalizeEventId(data["event_id"]);
    if (eventId) {
      return `event:${eventId}`;
    }
    const type = this.resolveNotificationType(data) ?? "unknown";
    const demandeId = this.resolveDemandeId(data) ?? "none";
    return `fallback:${type}:${demandeId}`;
  }

  private isDuplicatePushNavigation(data: Record<string, any>): boolean {
    this.prunePushDedupCache();
    const key = this.buildPushDedupKey(data);
    const ts = this.recentPushNavKeys.get(key);
    return ts != null && Date.now() - ts < NOTIFICATION_SOUND_DEDUP_TTL_MS;
  }

  private markPushNavigationHandled(data: Record<string, any>): void {
    this.recentPushNavKeys.set(this.buildPushDedupKey(data), Date.now());
  }

  private prunePushDedupCache(): void {
    const now = Date.now();
    for (const [key, ts] of this.recentPushNavKeys) {
      if (now - ts > NOTIFICATION_SOUND_DEDUP_TTL_MS) {
        this.recentPushNavKeys.delete(key);
      }
    }
  }

  /**
   * Valide rôle, existence API et cohérence métier avant navigation.
   * Évite écran blanc / crash sur notifications anciennes ou payload incomplet.
   */
  private async guardNavigationPath(
    path: string,
    data: Record<string, any>,
    type: string | undefined
  ): Promise<string | null> {
    if (!this.isPathRoleCompatible(path)) {
      await this.navigateToSafeHome("role_mismatch");
      return null;
    }

    if (type && !this.isEventRoleCompatible(type)) {
      await this.navigateToSafeHome("role_mismatch");
      return null;
    }

    const demandeId = this.resolveDemandeId(data) ?? this.extractIdFromPath(path, "demande");
    const devisId = data["devis_id"] ?? this.extractIdFromPath(path, "devis");

    if (this.isPrestataire() && this.isPrestataireDemandTarget(path, type, data)) {
      if (demandeId != null) {
        const result = await this.validatePrestataireDemande(demandeId, data);
        if (result !== "ok") {
          await this.navigateToSafeHome(result);
          return null;
        }
        if (devisId != null && this.shouldPreferPayloadDevisId(type, data, path)) {
          const payloadDevisPath = await this.resolvePrestataireDevisTarget(
            devisId,
            data
          );
          if (payloadDevisPath) {
            return payloadDevisPath;
          }
          await this.navigateToSafeHome("unavailable");
          return null;
        }
        const loaded = this.localStorageService.getCurrentDemand();
        const demande = extractDemandeForRoute(loaded);
        const resolved = resolvePrestataireDemandRoute(demande);
        if (resolved) {
          return resolved;
        }
        return "/prestataire/demandes";
      }
      if (devisId != null && type && PRESTATAIRE_DEVIS_EVENTS.has(type)) {
        const resolvedDevisPath = await this.resolvePrestataireDevisTarget(
          devisId,
          data
        );
        if (resolvedDevisPath) {
          return resolvedDevisPath;
        }
        await this.navigateToSafeHome("unavailable");
        return null;
      }
      if (!this.hasPrestataireLocalDemand()) {
        await this.navigateToSafeHome("unavailable");
        return null;
      }
    }

    const prestataireDevisMatch = path.match(/^\/prestataire\/devis-resume\/(\d+)/);
    if (prestataireDevisMatch) {
      const id = Number(prestataireDevisMatch[1] ?? devisId);
      const result = await this.validateClientDevis(id);
      if (result !== "ok") {
        await this.navigateToSafeHome(result);
        return null;
      }
    }

    const clientAroundMatch = path.match(/^\/particulier\/services-around\/(\d+)/);
    if (clientAroundMatch) {
      const result = await this.validateClientDemande(Number(clientAroundMatch[1]));
      if (result === "finished") {
        await this.navigateToSafeHome("finished");
        return null;
      }
      if (result !== "ok") {
        await this.navigateToSafeHome(result);
        return null;
      }
    }

    const clientDevisMatch = path.match(/^\/particulier\/devis\/(\d+)/);
    if (clientDevisMatch) {
      const id = Number(clientDevisMatch[1] ?? devisId);
      const result = await this.validateClientDevis(id);
      if (result !== "ok") {
        await this.navigateToSafeHome(result);
        return null;
      }
    }

    if (
      this.isPrestataire() &&
      path === "/prestataire" &&
      demandeId != null &&
      this.isPreAssignEvent(type)
    ) {
      const result = await this.validatePrestataireDemande(demandeId, data);
      if (result !== "ok") {
        await this.navigateToSafeHome(result);
        return null;
      }
    }

    return path;
  }

  private shouldPreferPayloadDevisId(
    type: string | undefined,
    data: Record<string, any>,
    path: string
  ): boolean {
    if (type && PRESTATAIRE_DEVIS_EVENTS.has(type)) {
      return true;
    }
    if (data["navigation_screen"] === "devis_detail") {
      return true;
    }
    return (
      path.includes("/devis-resume/") || path.includes("/demande-images/")
    );
  }

  private async resolvePrestataireDevisTarget(
    devisId: string | number,
    data: Record<string, any>
  ): Promise<string | null> {
    const numericDevisId = Number(devisId);
    const result = await this.validateClientDevis(numericDevisId);
    if (result !== "ok") {
      return null;
    }

    const demandeId = this.resolveDemandeId(data);
    if (demandeId != null) {
      const demandResult = await this.validatePrestataireDemande(demandeId, data);
      if (demandResult !== "ok") {
        return null;
      }
    }

    const demande = extractDemandeForRoute(this.localStorageService.getCurrentDemand());
    if ((demande?.imagePreuveBefore?.length ?? 0) > 0) {
      return `/prestataire/demande-images/${devisId}`;
    }

    return resolvePrestataireDevisResumePath(devisId);
  }

  private isPrestataireDemandTarget(
    path: string,
    type: string | undefined,
    data: Record<string, any>
  ): boolean {
    if (path.startsWith("/prestataire/demandes")) {
      return true;
    }
    if (path.startsWith("/prestataire/devis-resume") || path.startsWith("/prestataire/demande-images")) {
      return true;
    }
    if (type && PRESTATAIRE_DEMAND_ROUTE_EVENTS.has(type)) {
      return true;
    }
    const screen = data["navigation_screen"];
    return screen === "demande_detail" || screen === "devis_detail";
  }

  private isPreAssignEvent(type: string | undefined): boolean {
    return type === "NEW_DEMANDE" || type === "NEW_PRE_ASSIGN_DEMANDE";
  }

  private hasPrestataireLocalDemand(): boolean {
    const current = this.localStorageService.getCurrentDemand();
    return !!(current?.demande ?? current);
  }

  private isPathRoleCompatible(path: string): boolean {
    if (path.startsWith("/particulier") && this.isPrestataire()) {
      return false;
    }
    if (path.startsWith("/prestataire") && !this.isPrestataire()) {
      return false;
    }
    return true;
  }

  private isEventRoleCompatible(type: string): boolean {
    if (PRESTATAIRE_ONLY_EVENTS.has(type) && !this.isPrestataire()) {
      return false;
    }
    if (CLIENT_ONLY_EVENTS.has(type) && this.isPrestataire()) {
      return false;
    }
    return true;
  }

  private async validatePrestataireDemande(
    demandeId: string | number,
    data: Record<string, any>
  ): Promise<GuardResult> {
    try {
      const loaded = await this.providerService.storeAndDemandData(
        { ...data, demande_id: demandeId },
        { forceRefresh: true }
      );
      const demande = loaded?.demande ?? loaded;
      if (!demande) {
        return "unavailable";
      }
      const statut = demande?.statut ?? demande?.status;
      if (statut === "CANCELED") {
        return "canceled";
      }
      if (statut === "FINISHED") {
        return "finished";
      }
      return "ok";
    } catch {
      return "unavailable";
    }
  }

  private async validateClientDemande(demandeId: number): Promise<GuardResult> {
    if (!demandeId || Number.isNaN(demandeId)) {
      return "unavailable";
    }
    try {
      const res = await firstValueFrom(this.clientService.getDemandeById(demandeId));
      const demande = res?.body as Record<string, any> | undefined;
      if (!demande) {
        return "unavailable";
      }
      const statut = demande["statut"] ?? demande["status"];
      if (statut === "CANCELED") {
        try {
          this.localStorageService.dropClientDemand();
        } catch {
          // ignore
        }
        return "canceled";
      }
      if (statut === "FINISHED") {
        return "finished";
      }
      return "ok";
    } catch {
      return "unavailable";
    }
  }

  private async validateClientDevis(devisId: number): Promise<GuardResult> {
    if (!devisId || Number.isNaN(devisId)) {
      return "unavailable";
    }
    try {
      await firstValueFrom(this.providerService.getDevisInfo(devisId));
      return "ok";
    } catch {
      return "unavailable";
    }
  }

  private extractIdFromPath(
    path: string,
    kind: "demande" | "devis"
  ): string | number | undefined {
    if (kind === "demande") {
      const aroundMatch = path.match(/\/services-around\/(\d+)/);
      if (aroundMatch?.[1]) {
        return aroundMatch[1];
      }
      const demandeMatch = path.match(/\/demande\/(\d+)/);
      return demandeMatch?.[1];
    }
    const devisMatch =
      path.match(/\/devis-resume\/(\d+)/) ??
      path.match(/\/devis\/(\d+)/);
    return devisMatch?.[1];
  }

  private async navigateToSafeHome(reason: GuardResult): Promise<void> {
    const messageKey =
      reason === "role_mismatch"
        ? "NOTIFICATIONS.PUSH_NAV.ROLE_MISMATCH"
        : reason === "canceled"
          ? "NOTIFICATIONS.PUSH_NAV.DEMAND_CANCELED"
          : reason === "finished"
            ? "NOTIFICATIONS.PUSH_NAV.DEMAND_FINISHED"
            : "NOTIFICATIONS.PUSH_NAV.STALE_OR_UNAVAILABLE";

    const message = this.translate.instant(messageKey);
    void this.bobToast.warningBottom(message, { duration: 4000 });

    const fallback =
      reason === "finished"
        ? this.isPrestataire()
          ? "/prestataire/track-demands"
          : "/particulier/track-demands"
        : this.homePath();

    await this.navigationPrep.prepareForExternalNavigation("push");

    try {
      await this.router.navigateByUrl(fallback, { replaceUrl: true });
    } catch (e) {
      console.warn("[PushNavigationService] safe home navigation failed:", e);
    }
  }

  /**
   * Priorité routing (cf. universal_links.md) :
   * 1. deep_link / universal_link
   * 2. navigation_screen + navigation_params
   * 3. event_type + IDs métier
   */
  private resolvePath(data: Record<string, any>, type: string | undefined): string | null {
    const fromDeepLink = this.resolveFromDeepLink(data["deep_link"]);
    if (fromDeepLink) {
      return fromDeepLink;
    }

    const fromUniversal = this.resolveFromUniversalLink(data["universal_link"]);
    if (fromUniversal) {
      return fromUniversal;
    }

    const fromScreen = this.resolveFromNavigationScreen(
      data["navigation_screen"],
      this.parseNavigationParams(data["navigation_params"]),
      type
    );
    if (fromScreen) {
      return fromScreen;
    }

    return this.resolveFallbackPath(data, type);
  }

  private normalizePayload(data: Record<string, any>): Record<string, any> {
    const payload = { ...data };
    if (payload["demande_id"] == null && payload["id"] != null && payload["event_type"] !== "NEW_ANNOUNCE") {
      payload["demande_id"] = payload["id"];
    }
    if (payload["announce_id"] == null && payload["id"] != null && payload["event_type"] === "NEW_ANNOUNCE") {
      payload["announce_id"] = payload["id"];
    }
    return payload;
  }

  private parseNavigationParams(raw: unknown): NavigationParams {
    if (raw == null || raw === "") {
      return {};
    }
    if (typeof raw === "object" && !Array.isArray(raw)) {
      const out: NavigationParams = {};
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (v != null) {
          out[k] = String(v);
        }
      }
      return out;
    }
    if (typeof raw !== "string") {
      return {};
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return this.parseNavigationParams(parsed);
      }
    } catch {
      // ignore invalid JSON
    }
    return {};
  }

  private resolveFromDeepLink(raw: unknown): string | null {
    if (typeof raw !== "string" || !raw.trim()) {
      return null;
    }
    return this.parseBobExpressUrl(raw.trim());
  }

  private resolveFromUniversalLink(raw: unknown): string | null {
    if (typeof raw !== "string" || !raw.trim()) {
      return null;
    }
    try {
      const url = new URL(raw.trim());
      const path = url.pathname.replace(/^\/+/, "");
      if (!path) {
        return this.homePath();
      }
      return this.parseBobExpressPath(path.split("/").filter(Boolean));
    } catch {
      return null;
    }
  }

  private parseBobExpressUrl(link: string): string | null {
    if (link.startsWith(`${DEEP_LINK_SCHEME}://`)) {
      const path = link.slice(`${DEEP_LINK_SCHEME}://`.length).split("?")[0];
      return this.parseBobExpressPath(path.split("/").filter(Boolean));
    }
    if (link.startsWith("/particulier") || link.startsWith("/prestataire")) {
      return link.split("?")[0];
    }
    return null;
  }

  private parseBobExpressPath(segments: string[]): string | null {
    if (segments.length === 0) {
      return this.homePath();
    }

    const [head, ...rest] = segments;

    if (head === "home") {
      return this.homePath();
    }

    if (head === "demande") {
      const demandeId = rest[0];
      if (!demandeId) {
        return null;
      }
      if (rest[1] === "devis" && rest[2]) {
        return this.devisPath(rest[2], demandeId);
      }
      return this.demandePath(demandeId);
    }

    if (head === "devis" && rest[0]) {
      return this.devisPath(rest[0]);
    }

    if (head === "dispatch" && rest[0] === "offer") {
      return "/prestataire";
    }

    if (head === "discussion" && rest[0]) {
      return this.messageriePath(rest[0]);
    }

    if (head === "support" && rest[0] === "message") {
      return this.messageriePath(rest[1]);
    }

    if (head === "announce") {
      return this.homePath();
    }

    return this.homePath();
  }

  private resolveFromNavigationScreen(
    screen: unknown,
    params: NavigationParams,
    type: string | undefined
  ): string | null {
    if (typeof screen !== "string" || !screen) {
      return null;
    }

    const demandeId = params["demande_id"];
    const devisId = params["devis_id"];
    const discussionId = params["discussion_id"];

    switch (screen) {
      case "home":
        return this.homePath();
      case "dispatch_offer":
        return "/prestataire";
      case "demande_suivi":
        return demandeId ? `/particulier/services-around/${demandeId}` : null;
      case "demande_detail":
        return this.demandePath(demandeId);
      case "devis_detail":
        return this.devisPath(devisId, demandeId);
      case "discussion":
        return this.messageriePath(discussionId);
      case "support_message":
        return this.messageriePath(params["message_id"] ?? discussionId);
      case "announce_detail":
        return this.homePath();
      default:
        return type ? this.resolveFallbackPath({ ...params, navigation_screen: screen }, type) : null;
    }
  }

  private demandePath(demandeId: string | number | undefined): string | null {
    if (demandeId == null || demandeId === "") {
      return this.isPrestataire() ? "/prestataire/demandes" : null;
    }
    if (this.isPrestataire()) {
      return "/prestataire/demandes";
    }
    return `/particulier/services-around/${demandeId}`;
  }

  private clientAwaitDevisPath(
    demandeId: string | number | undefined
  ): string | null {
    if (demandeId == null || demandeId === "") {
      return "/particulier";
    }
    return `/particulier/services-around/${demandeId}?awaitDevis=1`;
  }

  private resolveApplyProviderDevisPath(
    data: Record<string, any>,
    demandeId: string | number | undefined
  ): string | null {
    const applyType = String(data["type"] ?? "").toUpperCase();
    if (applyType === "SIGNAL" || applyType === "RELANCE") {
      return this.clientAwaitDevisPath(demandeId);
    }
    return this.devisPath(data["devis_id"] ?? demandeId, demandeId);
  }

  private devisPath(devisId: string | number | undefined, demandeId?: string | number): string | null {
    if (this.isPrestataire()) {
      const prestatairePath = resolvePrestataireDevisResumePath(devisId);
      if (prestatairePath) {
        return prestatairePath;
      }
      return demandeId != null && demandeId !== ""
        ? "/prestataire/demandes"
        : null;
    }

    const id = devisId ?? demandeId;
    if (id == null || id === "") {
      return null;
    }
    return `/particulier/devis/${id}`;
  }

  private messageriePath(conversationId?: string): string {
    const prefix = this.isPrestataire()
      ? "/prestataire/messagerie"
      : "/particulier/messagerie";
    return conversationId ? `${prefix}/${conversationId}` : prefix;
  }

  private homePath(): string {
    return this.isPrestataire() ? "/prestataire" : "/particulier";
  }

  private isPrestataire(): boolean {
    return (this.localStorageService.getUserRole() || "").toLowerCase() === "prestataire";
  }

  private resolveFallbackPath(
    data: Record<string, any>,
    type: string | undefined
  ): string | null {
    const demande_id = this.resolveDemandeId(data);
    const discussion_id = data["discussion_id"];
    const messageriePrefix = this.isPrestataire()
      ? "/prestataire/messagerie"
      : "/particulier/messagerie";

    switch (type) {
      case "NEW_DEMANDE":
      case "NEW_PRE_ASSIGN_DEMANDE":
        return this.isPrestataire() ? "/prestataire" : null;
      case "ASSIGNED_DEMANDE_CLIENT":
      case "PROVIDER_ACCEPTED_DEMANDE":
        return demande_id ? `/particulier/services-around/${demande_id}` : null;
      case "CANCELED_PRESTATAIRE_DEMANDE":
      case "DESTINATION_ATTENT_CLIENT":
      case "ANNULATION_DEMANDE":
        return demande_id ? `/particulier/services-around/${demande_id}` : null;
      case "ADMIN_CANCELED_DEMANDE":
        if (this.isPrestataire()) {
          return "/prestataire";
        }
        if (demande_id) {
          return `/particulier/services-around/${demande_id}`;
        }
        return "/particulier";
      case "ONROAD_DEMANDE_CANCELED":
        return "/prestataire";
      case "INFORM_PRESTATAIRE_DEMANDE":
      case "SIGNAL_PROBLEM_PROVIDER":
      case "CONFIRM_ARRIVAL_PRESTATAIRE":
        return demande_id ? "/prestataire/demandes" : "/prestataire";
      case "SIGNAL_PROBLEM_CLIENT":
        return this.clientAwaitDevisPath(demande_id);
      case "CHANGE_STATUT_DEVIS":
      case "CREATION_DEVIS_CLIENT":
        return this.devisPath(data["devis_id"] ?? demande_id, demande_id);
      case "APPLY_PROVIDER_DEVIS":
        return this.resolveApplyProviderDevisPath(data, demande_id);
      case "DISCUSSION":
        return discussion_id ? `${messageriePrefix}/${discussion_id}` : messageriePrefix;
      case "ADMIN_MESSAGE":
        return messageriePrefix;
      case "NEW_ANNOUNCE":
        return "/particulier";
      case "POP_PRE_ASSIGN_COMMAND":
        return this.isPrestataire() ? "/prestataire" : null;
      default:
        return null;
    }
  }

  private applySideEffects(data: Record<string, any>, type: string | undefined): void {
    switch (type) {
      case "NEW_PRE_ASSIGN_DEMANDE":
      case "NEW_DEMANDE":
        this.storeIncomingPreAssignedDemand(data);
        break;
      case "CANCELED_PRESTATAIRE_DEMANDE":
        try {
          this.localStorageService.dropClientDemand();
        } catch (e) {
          console.warn("[PushNavigationService] dropClientDemand failed:", e);
        }
        break;
      case "ADMIN_CANCELED_DEMANDE":
        if (this.isPrestataire()) {
          try {
            this.localStorageService.dropCurrentDemand();
          } catch (e) {
            console.warn("[PushNavigationService] dropCurrentDemand failed:", e);
          }
        } else {
          try {
            this.localStorageService.dropClientDemand();
          } catch (e) {
            console.warn("[PushNavigationService] dropClientDemand failed:", e);
          }
        }
        break;
      case "ONROAD_DEMANDE_CANCELED":
        try {
          this.localStorageService.dropCurrentDemand();
        } catch (e) {
          console.warn("[PushNavigationService] dropCurrentDemand failed:", e);
        }
        break;
      default:
        break;
    }
  }

  private storeIncomingPreAssignedDemand(data: Record<string, any>): void {
    if (!this.isPrestataire()) {
      return;
    }
    const payload = this.buildDemandPayload(data);
    this.providerService.storeAndDemandData(payload).catch((error) => {
      console.warn("[PushNavigationService] store incoming pre-assigned demand failed:", error);
    });
  }

  private buildDemandPayload(data: Record<string, any>): Record<string, any> {
    const demandRef = this.resolveDemandReference(data);
    const payload: Record<string, any> = { ...data };
    if (payload["demande"] == null && demandRef != null) {
      payload["demande"] = demandRef;
    }
    if (
      payload["demande_id"] == null &&
      (typeof demandRef === "number" || typeof demandRef === "string")
    ) {
      payload["demande_id"] = demandRef;
    }
    return payload;
  }

  private resolveDemandReference(data: Record<string, any>): unknown {
    return (
      data["demande"] ??
      data["demande_id"] ??
      data["demand_id"] ??
      data["id_demande"] ??
      data["id"]
    );
  }

  private resolveDemandeId(data: Record<string, any>): string | number | undefined {
    const demande = data["demande"];
    const demandeObject =
      demande && typeof demande === "object" ? (demande as Record<string, any>) : null;
    return (
      data["demande_id"] ??
      data["demand_id"] ??
      data["id_demande"] ??
      demandeObject?.["id"] ??
      (typeof demande === "number" || typeof demande === "string" ? demande : data["id"])
    );
  }

  private resolveNotificationType(
    data: Record<string, any> | null | undefined
  ): string | undefined {
    return data?.["type_event"] ?? data?.["event_type"] ?? data?.["type_notification"];
  }

  private summarizePushData(data: Record<string, any>): Record<string, unknown> {
    return {
      type_event: this.resolveNotificationType(data),
      deep_link: data?.["deep_link"],
      navigation_screen: data?.["navigation_screen"],
      should_refresh: data?.["should_refresh"],
      demande_id: this.resolveDemandeId(data),
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
